import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import ExploreMore from "@/components/ExploreMore";
import useCanonical from "@/hooks/useCanonical";
import useMetaDescription from "@/hooks/useMetaDescription";

const CITIES = [
  { slug: "mumbai", name: "Mumbai", state: "Maharashtra", projects: "100+" },
  { slug: "navi-mumbai", name: "Navi Mumbai", state: "Maharashtra", projects: "50+" },
  { slug: "thane", name: "Thane", state: "Maharashtra", projects: "35+" },
  { slug: "pune", name: "Pune", state: "Maharashtra", projects: "80+" },
  { slug: "ahmedabad", name: "Ahmedabad", state: "Gujarat", projects: "45+" },
  { slug: "bangalore", name: "Bangalore", state: "Karnataka", projects: "70+" },
  { slug: "hyderabad", name: "Hyderabad", state: "Telangana", projects: "55+" },
  { slug: "chennai", name: "Chennai", state: "Tamil Nadu", projects: "40+" },
  { slug: "delhi", name: "Delhi", state: "NCR", projects: "60+" },
  { slug: "gurugram", name: "Gurugram", state: "Haryana", projects: "75+" },
  { slug: "noida", name: "Noida", state: "Uttar Pradesh", projects: "50+" },
];

export default function LocationsPage() {
  useCanonical("https://ezeefitofficesystems.in/locations");
  useMetaDescription("Ezeefit delivers modular office furniture across Mumbai, Navi Mumbai, Thane, Pune, Bangalore, Delhi NCR & 50+ cities — pan-India install & support.");
  useEffect(() => {
    document.title = "Office Furniture Locations | Mumbai, Navi Mumbai, Thane, Pune & Pan India | Ezeefit";
  }, []);

  return (
    <div className="pt-20" data-testid="locations-page">
      <Helmet>
        <meta name="description" content="Ezeefit delivers modular office furniture across Mumbai, Navi Mumbai, Thane, Pune, Bangalore, Delhi NCR & 50+ cities — pan-India install & support." />
        <link rel="canonical" href="https://ezeefitofficesystems.in/locations" />
      </Helmet>
      {/* Hero */}
      <section className="py-24 bg-[#0F172A] noise-overlay">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white mb-8 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Home
            </Link>
            
            <div className="text-center">
              <span className="font-mono text-sm text-[#F97316] tracking-wider uppercase mb-4 block">
                Pan-India Presence
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                Office Furniture Solutions Across India
              </h1>
              <p className="text-lg text-[#94A3B8] max-w-3xl mx-auto">
                Ezeefit Office Systems delivers modular office furniture and workspace solutions 
                to corporate offices in major cities across India. Find your city below.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-24 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {CITIES.map((city, index) => (
              <motion.div
                key={city.slug}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/locations/${city.slug}`}
                  className="block bg-white border border-[#E2E8F0] p-6 hover:border-[#F97316] hover:shadow-lg transition-all group"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 text-[#F97316] mb-2">
                        <MapPin className="w-5 h-5" />
                        <span className="text-sm font-mono uppercase">{city.state}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-[#0F172A] group-hover:text-[#F97316] transition-colors">
                        {city.name}
                      </h3>
                      <p className="text-[#64748B] mt-2">
                        Office furniture supply & installation
                      </p>
                    </div>
                    <ArrowRight className="w-6 h-6 text-[#E2E8F0] group-hover:text-[#F97316] group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Verifiable Credentials */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl font-bold text-[#F97316]">2014</p>
              <p className="text-[#94A3B8] mt-1">Established</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#F97316]">ISO 9001:2015</p>
              <p className="text-[#94A3B8] mt-1">Certified</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#F97316]">Mumbai</p>
              <p className="text-[#94A3B8] mt-1">Own Factory</p>
            </div>
            <div>
              <p className="text-4xl font-bold text-[#F97316]">Pan-India</p>
              <p className="text-[#94A3B8] mt-1">Project Execution</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
            Don't See Your City?
          </h2>
          <p className="text-lg text-[#64748B] mb-8">
            We deliver office furniture solutions across India. 
            Contact us to discuss your project requirements.
          </p>
          <Button
            asChild
            className="bg-[#F97316] hover:bg-orange-600 text-white rounded-none px-10 py-6 font-semibold uppercase tracking-wider"
          >
            <Link to="/contact">
              Contact Us
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>

      <ExploreMore currentPage="locations" />
    </div>
  );
}
